import { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useForm } from "react-hook-form";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { toast } from "react-toastify";
import CloseTicketForm from "../../../pages/CloseTicketForm";
import Form from "react-bootstrap/Form";
import { Row, Col, Spinner } from "react-bootstrap";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useCommonContext } from "../../../utils/context";

const MAX_IMAGES = 3;

function UpdateTicketsStatus({
  show,
  handleClose,
  ticket,
  fetchTickets,
  action,
}) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [formData, setFormData] = useState({ images: [], before_note: "" });
  const [showCloseTicketForm, setShowCloseTicketForm] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [closeTicketFormSaved, setCloseTicketFormSaved] = useState(false);
  const inputRefs = useRef([]);
  const { fetchTicketStatus } = useCommonContext;
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const initializeImages = () => {
    const initialImages = Array.from({ length: MAX_IMAGES }, (_, index) => {
      const fieldName = `before_task_images_${index + 1}`;
      return {
        id: index + 1,
        file: ticket ? ticket[fieldName] : null,
        fieldName: fieldName,
      };
    });

    setFormData({ ...formData, images: initialImages });
  };

  useEffect(() => {
    if (ticket) {
      initializeImages();
      reset({
        status: ticket.status || "",
        onhold_note: ticket.onhold_note || "",
        rejected_note: ticket.rejected_note || "",
        acceptedNote: ticket.acceptedNote || "",
        autoclose_note: ticket.autoclose_note || "",
        before_note: ticket.before_note || "",
      });
      setStatus(ticket.status || "");
    } else {
      reset({});
      setStatus("");
      setFormData({ images: [], before_note: "" });
    }
  }, [show, ticket, reset]);

  useEffect(() => {
    switch (ticket.status) {
      case "Inactive":
        setStatusOptions(["Inactive", "Accepted", "Rejected", "Not-Assigned"]);
        break;
      case "Not-Assigned":
        setStatusOptions(["Not-Assigned","Inactive", "Accepted", "Rejected"]);
        break;
      case "Accepted":
        setStatusOptions(["Accepted", "Ongoing", "Rejected", "Autoclose"]);
        break;
      case "Ongoing":
        setStatusOptions(["Ongoing", "On-Hold", "Completed", "Autoclose"]);
        break;
      case "On-Hold":
        setStatusOptions(["On-Hold", "Ongoing", "Completed", "Autoclose"]);
        break;
      default:
        setStatusOptions([
          "Completed",
          "Autoclose",
          "Ongoing",
          "Rejected",
          "Inactive",
          "On-Hold",
          "Accepted",
          "Not-Assigned",
        ]);
    }
  }, [ticket.status]);

  const handleChange = (e) => {
    const { name, files, value } = e.target;

    if (name.startsWith("image")) {
      const id = parseInt(name.split("_")[1], 10);

      const file = files[0];
      if (file) {
        const validImageTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/webp",
        ];
        if (!validImageTypes.includes(file.type)) {
          toast.error(
            "Invalid file type. Only JPG, PNG, or WebP images are allowed."
          );
          return;
        }
      }

      const updatedImages = formData.images.map((img) =>
        img.id === id ? { ...img, file } : img
      );
      setFormData({ ...formData, images: updatedImages });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleImageClick = (id) => {
    inputRefs.current[id - 1].click();
  };

  const handleFormSubmit = async (data) => {
    if (status === "Ongoing" && !formData.images.some((image) => image.file)) {
      toast.error("At least one image is required for ongoing status.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("status", data.status);
    formDataToSend.append("onhold_note", data.onhold_note || "");
    formDataToSend.append("rejected_note", data.rejected_note || "");
    formDataToSend.append("acceptedNote", data.acceptedNote || "");
    formDataToSend.append("autoclose_note", data.autoclose_note || "");

    if (data.status === "Ongoing") {
      formDataToSend.append("before_note", data.before_note || "");
      formData.images.forEach((image, index) => {
        if (image?.file && typeof image?.file === "object") {
          formDataToSend.append(`before_task_images_${index + 1}`, image.file);
        }
      });
    }

    let apiUrl = `${BASE_URL}/api/tickets/${ticket.id}/`;
    let apiMethod = axios.put;

    try {
      setLoading(true);
      const response = await apiMethod(apiUrl, formDataToSend, {
        headers: { ...HEADERS, "Content-Type": "multipart/form-data" },
      });
      if (response) {
        handleClose();

        toast.success("Ticket Status Updated Successfully");
        fetchTickets();
        fetchTicketStatus();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage);

    
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "Completed") {
      setShowCloseTicketForm(true);
    } else {
      setShowCloseTicketForm(false);
    }
  }, [status]);

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            Update Ticket Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="row mb-3">
              <div className="col-md-12">
                <label htmlFor="status" className="form-label">
                  Status *
                </label>
                <select
                  className="form-select"
                  id="status"
                  {...register("status", { required: "Status is required" })}
                  value={status}
                  onChange={(e) => {
                    const selectedStatus = e.target.value;
                    setStatus(selectedStatus);
                    setValue("onhold_note", "");
                    setValue("rejected_note", "");
                    setValue("before_note", "");
                    setValue("acceptedNote", "");
                    setValue("autoclose_note", "");
                  }}
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <div className="text-danger">{errors.status.message}</div>
                )}
              </div>
            </div>

            {status === "Rejected" && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="rejected_note" className="form-label">
                    Reason*
                  </label>
                  <textarea
                    id="rejected_note"
                    className="form-control"
                    {...register("rejected_note", {
                      required: "Reason for Rejection is required",
                    })}
                  />
                  {errors.rejected_note && (
                    <div className="text-danger">
                      {errors.rejected_note.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {status === "On-Hold" && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="onhold_note" className="form-label">
                    Reason*
                  </label>
                  <textarea
                    id="onhold_note"
                    className="form-control"
                    {...register("onhold_note", {
                      required: "Reason for Hold is required",
                    })}
                  />
                  {errors.onhold_note && (
                    <div className="text-danger">
                      {errors.onhold_note.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {status === "Accepted" && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="acceptedNote" className="form-label">
                    Remark*
                  </label>
                  <textarea
                    id="acceptedNote"
                    className="form-control"
                    {...register("acceptedNote", {
                      required: "Remark for acceptence is required",
                    })}
                  />
                  {errors.acceptedNote && (
                    <div className="text-danger">
                      {errors.acceptedNote.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {status === "Autoclose" && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="autoclose_note" className="form-label">
                    Remark*
                  </label>
                  <textarea
                    id="autoclose_note"
                    className="form-control"
                    {...register("autoclose_note", {
                      required: "Remark for acceptence is required",
                    })}
                  />
                  {errors.autoclose_note && (
                    <div className="text-danger">
                      {errors.autoclose_note.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {status === "Ongoing" && (
              <>
                <div className="upload-photo-form">
                  <p>
                    Please upload pictures before starting the task (Atleast one
                    is required)
                  </p>
                  <Form>
                    <Row className="align-items-center mb-3">
                      {formData?.images?.map((field, index) => (
                        <Col xs={4} key={field.id} className="text-center mb-3">
                          <div
                            className="image-upload-container border rounded p-3 position-relative d-flex flex-column align-items-center"
                            onClick={() => handleImageClick(field.id)}
                            style={{
                              height: "140px",
                              cursor: "pointer",
                              overflow: "hidden",
                            }}
                          >
                            <Form.Group
                              controlId={`formFile${field.id}`}
                              className="mb-3 w-100 h-100 d-flex align-items-center justify-content-center"
                            >
                              <Form.Label
                                className="d-flex flex-column align-items-center justify-content-center w-100 h-100"
                                style={{ cursor: "pointer" }}
                              >
                                {field.file ? (
                                  <img
                                    src={
                                      field.file instanceof File
                                        ? URL.createObjectURL(field.file)
                                        : field.file
                                    }
                                    alt="Upload"
                                    className="img-fluid"
                                    style={{
                                      maxHeight: "100%",
                                      maxWidth: "100%",
                                      objectFit: "cover",
                                      borderRadius: "5px",
                                    }}
                                  />
                                ) : (
                                  <div className="text-center align-items-center justify-content-center">
                                    <FaCloudUploadAlt size={50} />
                                  </div>
                                )}
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/jpg,image/webp"
                                  className="form-control-file d-none"
                                  name={`image_${field.id}`}
                                  id={`image_${field.id}`}
                                  onChange={handleChange}
                                  ref={(el) => (inputRefs.current[index] = el)}
                                />
                              </Form.Label>
                            </Form.Group>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Form>
                  {formData.images.length === 0 && (
                    <div className="text-danger">
                      At least one image is required.
                    </div>
                  )}
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label htmlFor="before_note" className="form-label">
                      Note*
                    </label>
                    <textarea
                      id="before_note"
                      className="form-control"
                      {...register("before_note", {
                        required: "Note is required",
                      })}
                    />
                    {errors.before_note && (
                      <div className="text-danger">
                        {errors.before_note.message}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  loading || (status === "Completed" && !closeTicketFormSaved)
                }
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                  </>
                ) : (
                  "Update Status"
                )}
              </Button>
            </Modal.Footer>
          </form>
          {showCloseTicketForm && (
            <CloseTicketForm
              show={showCloseTicketForm}
              handleClose={() => {
                setShowCloseTicketForm(false);
                handleClose();
              }}
              onClose={() => {
                setCloseTicketFormSaved(true);
                handleClose();
              }}
              ticket={ticket}
              fetchTickets={fetchTickets}
              action={action}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default UpdateTicketsStatus;
